import type {
  DirectoryInvitation,
  DirectorySubmission,
  DirectorySubmissionReviewEvent,
  StoreDirectoryRow,
} from "@vimos/api-client";
import {
  buildInvitationUrl,
  computeInvitationExpiry,
  generateInvitationToken,
  hashInvitationToken,
  invitationTokenPrefix,
  normalizeDirectoryEditFeatures,
  normalizeDirectoryInvitationFeatures,
  type InvitationExpiryOption,
} from "@vimos/api-client";
import { supabase } from "../../lib/supabase";
import { getSupportedColumns } from "../../lib/schema";
export { uploadDirectoryImage } from "./image-upload";
import {
  mapDirectoryError,
  type DirectoryAdminPatch,
  type DirectoryFilters,
  type DirectoryStore,
  type DirectoryStats,
  type EditorialStatus,
} from "./directoryLib";

/** Base del sitio publico donde vive la pagina /invitacion/[token]. */
export const publicSiteUrl =
  (import.meta.env.VITE_PUBLIC_SITE_URL as string | undefined)?.replace(/\/+$/, "") ??
  "https://vimosdigital.com";

const REVIEW_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/directory-review`;
const SUBMISSIONS_BUCKET = "directory-submissions";

const baseSelectColumns = [
  "id",
  "saas_store_id",
  "slug",
  "name",
  "city",
  "category",
  "description",
  "store_url",
  "logo_url",
  "is_visible_in_directory",
  "is_force_hidden",
];

/** Columnas opcionales del esquema (migraciones manuales). */
const optionalSelectColumns = [
  "origin",
  "is_featured",
  "sort_order",
  "editorial_status",
  "synced_at",
  "updated_at",
  "category_id",
  "keywords",
];

function normalizeStoreRow(row: Record<string, unknown>): DirectoryStore {
  return {
    id: String(row["id"]),
    saas_store_id: row["saas_store_id"] == null ? null : String(row["saas_store_id"]),
    slug: String(row["slug"] ?? ""),
    name: String(row["name"] ?? ""),
    city: String(row["city"] ?? ""),
    category: String(row["category"] ?? ""),
    category_id: row["category_id"] == null ? null : String(row["category_id"]),
    keywords: Array.isArray(row["keywords"]) ? row["keywords"].map((keyword) => String(keyword)) : [],
    description: row["description"] == null ? null : String(row["description"]),
    store_url: String(row["store_url"] ?? ""),
    logo_url: row["logo_url"] == null ? null : String(row["logo_url"]),
    origin: row["origin"] === "manual" ? "manual" : "saas",
    is_visible_in_directory: Boolean(row["is_visible_in_directory"]),
    is_force_hidden: Boolean(row["is_force_hidden"]),
    is_featured: Boolean(row["is_featured"]),
    sort_order: Number(row["sort_order"] ?? 0),
    editorial_status: (["visible", "review", "hidden"] as const).includes(
      row["editorial_status"] as EditorialStatus,
    )
      ? (row["editorial_status"] as EditorialStatus)
      : "visible",
    synced_at: row["synced_at"] == null ? null : String(row["synced_at"]),
    updated_at: row["updated_at"] == null ? null : String(row["updated_at"]),
  };
}

export interface DirectoryListResult {
  stores: DirectoryStore[];
  supportedColumns: Set<string>;
}

export interface DirectoryWorkQueueResult extends DirectoryListResult {
  filteredTotal: number;
  stats: DirectoryStats;
}

export interface DirectoryStoreTarget {
  id: string;
  name: string;
  city: string;
  slug: string;
  origin: "manual" | "saas";
}

export type AdminPriorityBucket = "all" | "urgent" | "soon" | "follow_up";

export interface AdminPriorityItem {
  id: string;
  kind: "charge" | "submission" | "publication" | "follow_up";
  priority: "urgent" | "soon" | "follow_up";
  store_id: string | null;
  store_name: string;
  origin: "manual" | "saas" | null;
  title: string;
  detail: string;
  due_on: string | null;
  activity_at: string | null;
  amount: number | null;
  href: string;
}

export interface AdminPriorityQueue {
  items: AdminPriorityItem[];
  filteredTotal: number;
  counts: { total: number; urgent: number; soon: number; follow_up: number };
}

/** Unified operational inbox, filtered, counted and paginated by PostgreSQL. */
export async function fetchAdminPriorityQueue(input: {
  bucket: AdminPriorityBucket;
  search: string;
  page: number;
  today: string;
}): Promise<AdminPriorityQueue> {
  const { data, error } = await supabase.rpc(
    "admin_priority_work_queue" as never,
    {
      p_bucket: input.bucket,
      p_search: input.search,
      p_page: input.page,
      p_page_size: 30,
      p_today: input.today,
    } as never,
  );
  if (error) throw new Error(mapDirectoryError(error));
  const result = data as unknown as {
    items?: AdminPriorityItem[];
    filtered_total?: number;
    counts?: { total?: number; urgent?: number; soon?: number; follow_up?: number };
  };
  return {
    items: result.items ?? [],
    filteredTotal: Number(result.filtered_total ?? 0),
    counts: {
      total: Number(result.counts?.total ?? 0),
      urgent: Number(result.counts?.urgent ?? 0),
      soon: Number(result.counts?.soon ?? 0),
      follow_up: Number(result.counts?.follow_up ?? 0),
    },
  };
}

/** Página de directorio filtrada y ordenada en PostgreSQL, sin descargar toda la tabla. */
export async function fetchDirectoryWorkQueue(
  filters: DirectoryFilters,
  page: number,
): Promise<DirectoryWorkQueueResult> {
  const supportedColumns = await getSupportedColumns("stores_directory");
  const { data, error } = await supabase.rpc(
    "directory_admin_work_queue" as never,
    {
      p_search: filters.search,
      p_origin: filters.origin,
      p_visibility: filters.visibility,
      p_category: filters.category,
      p_editorial: filters.editorial,
      p_sort: filters.sort,
      p_page: page,
      p_page_size: 20,
    } as never,
  );
  if (error) throw new Error(mapDirectoryError(error));

  const result = data as unknown as {
    items?: Array<Record<string, unknown>>;
    filtered_total?: number;
    stats?: {
      total: number;
      published: number;
      pending_review: number;
      blocked: number;
      saas: number;
      manual: number;
    };
  };
  const stats = result.stats;
  return {
    stores: (result.items ?? []).map(normalizeStoreRow),
    supportedColumns,
    filteredTotal: Number(result.filtered_total ?? 0),
    stats: stats
      ? {
          total: Number(stats.total),
          published: Number(stats.published),
          pendingReview: Number(stats.pending_review),
          blocked: Number(stats.blocked),
          saas: Number(stats.saas),
          manual: Number(stats.manual),
        }
      : { total: 0, published: 0, pendingReview: 0, blocked: 0, saas: 0, manual: 0 },
  };
}

/** Busca acotada para el selector de fichas del enlace de edición. */
export async function searchDirectoryStoreTargets(search: string): Promise<DirectoryStoreTarget[]> {
  if (search.trim().length < 2) return [];
  const { data, error } = await supabase.rpc(
    "directory_admin_search_store_targets" as never,
    { p_search: search.trim(), p_limit: 20 } as never,
  );
  if (error) throw new Error(mapDirectoryError(error));
  return (data ?? []) as unknown as DirectoryStoreTarget[];
}

/** Loads one exact business for a preselected edit-link workflow. */
export async function fetchDirectoryStoreTarget(storeId: string): Promise<DirectoryStoreTarget> {
  const { data, error } = (await supabase
    .from("stores_directory")
    .select("id, name, city, slug, origin")
    .eq("id", storeId)
    .maybeSingle()) as unknown as {
    data: Pick<DirectoryStoreTarget, "id" | "name" | "city" | "slug" | "origin"> | null;
    error: unknown;
  };
  if (error) throw new Error(mapDirectoryError(error));
  if (!data) throw new Error("No se encontró el negocio seleccionado.");
  return {
    id: data.id,
    name: data.name,
    city: data.city ?? "",
    slug: data.slug,
    origin: data.origin === "manual" ? "manual" : "saas",
  };
}

/** Lista completa del directorio para el panel (una sola consulta). */
export async function fetchDirectoryStores(): Promise<DirectoryListResult> {
  const supportedColumns = await getSupportedColumns("stores_directory");
  const selectColumns = [
    ...baseSelectColumns,
    ...optionalSelectColumns.filter((column) => supportedColumns.has(column)),
  ].join(", ");

  let query = supabase.from("stores_directory").select(selectColumns);
  if (supportedColumns.has("is_featured")) query = query.order("is_featured", { ascending: false });
  if (supportedColumns.has("sort_order")) query = query.order("sort_order", { ascending: true });
  query = query.order("name", { ascending: true });

  const { data, error } = await query;
  if (error) throw new Error(mapDirectoryError(error));

  const rows = (data ?? []) as Array<Record<string, unknown>>;
  return { stores: rows.map(normalizeStoreRow), supportedColumns };
}

/** Ficha completa de un negocio para la pantalla de edicion. */
export async function fetchDirectoryStore(id: string): Promise<StoreDirectoryRow> {
  const { data, error } = await supabase.from("stores_directory").select("*").eq("id", id).maybeSingle();
  if (error) throw new Error(mapDirectoryError(error));
  if (!data) throw new Error("El negocio ya no existe en el directorio.");
  return data as StoreDirectoryRow;
}

export async function updateDirectoryStoreContext(input: {
  storeId: string;
  notes: string;
  nextAction: string;
  dueDate: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc(
    "directory_admin_update_360_context" as never,
    {
      p_store_id: input.storeId,
      p_notes: input.notes,
      p_next_action: input.nextAction,
      p_due_date: input.dueDate,
    } as never,
  );
  if (error) throw new Error(mapDirectoryError(error));
}

/**
 * Actualiza un negocio via la RPC `directory_admin_update`, que valida en el
 * servidor el rol, los campos permitidos segun el origen y las reglas de
 * publicacion. Nunca se escriben copias derivadas de tiendas SaaS.
 */
export async function updateDirectoryStore(id: string, patch: DirectoryAdminPatch): Promise<void> {
  const { error } = await supabase.rpc("directory_admin_update", {
    p_store_id: id,
    p_patch: patch,
  } as never);
  if (error) throw new Error(mapDirectoryError(error));
}

/** Elimina una ficha manual tras validación de rol y origen en PostgreSQL. */
export async function deleteManualDirectoryStore(id: string): Promise<void> {
  const { error } = await supabase.rpc(
    "directory_manual_store_delete" as never,
    {
      p_store_id: id,
    } as never,
  );
  if (error) throw new Error(mapDirectoryError(error));
}

/** Actualiza solo los perfiles sociales gestionados por Vimos, incluso en tiendas SaaS. */
export async function updateDirectorySocialLinks(id: string, links: Record<string, string>): Promise<void> {
  const { error } = await supabase.rpc("directory_admin_update_social_links", {
    p_store_id: id,
    p_social_links: links,
  } as never);
  if (error) throw new Error(mapDirectoryError(error));
}

export function slugify(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

async function uniqueSlug(name: string): Promise<string> {
  const root = slugify(name) || "negocio";
  let candidate = root;
  for (let attempt = 0; attempt < 50; attempt += 1) {
    const { data, error } = await supabase
      .from("stores_directory")
      .select("id")
      .eq("slug", candidate)
      .maybeSingle();
    if (error) throw error;
    if (!data) return candidate;
    candidate = `${root}-${attempt + 1}`;
  }
  return `${root}-${Date.now()}`;
}

export interface ManualStoreInput {
  name: string;
  city: string;
  category: string;
  category_id?: string | null;
  keywords?: string[];
  description?: string | null;
  department?: string | null;
  province?: string | null;
  district?: string | null;
  address?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
  website_url?: string | null;
  google_maps_url?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  logo_url?: string | null;
  cover_image_url?: string | null;
  gallery?: string[];
  social_links?: Record<string, string>;
  billing_plan_id?: string | null;
  billing_plan_name?: string | null;
  billing_plan_price?: number | null;
  billing_plan_period?: "monthly" | "yearly" | "one_time" | null;
  billing_plan_snapshot?: Record<string, unknown> | null;
  billing_is_comped?: boolean;
  billing_price_note?: string | null;
  billing_setup_amount?: number;
  billing_setup_due_date?: string | null;
  billing_monthly_amount?: number;
  billing_yearly_amount?: number;
  billing_start_date?: string | null;
  publish: boolean;
}

/** Inserta un negocio manual (origen manual, sin id del SaaS). */
export async function createManualStore(input: ManualStoreInput): Promise<StoreDirectoryRow> {
  const columns = await getSupportedColumns("stores_directory");
  const slug = await uniqueSlug(input.name);
  if (input.billing_plan_id && !columns.has("billing_plan_id")) {
    throw new Error("Aplica primero la migración de planes y acuerdos del directorio en Supabase.");
  }
  if ((input.billing_yearly_amount ?? 0) > 0 && !columns.has("billing_yearly_amount")) {
    throw new Error("Aplica primero la migración de cobros anuales del directorio en Supabase.");
  }
  if (input.billing_is_comped && !columns.has("billing_is_comped")) {
    throw new Error("Aplica primero la migración de cortesías gratuitas del directorio en Supabase.");
  }
  if (
    input.billing_is_comped &&
    ((input.billing_setup_amount ?? 0) > 0 ||
      (input.billing_monthly_amount ?? 0) > 0 ||
      (input.billing_yearly_amount ?? 0) > 0)
  ) {
    throw new Error("Una cortesía gratuita no puede tener importes de cobro.");
  }
  if ((input.billing_yearly_amount ?? 0) > 0 && (input.billing_monthly_amount ?? 0) > 0) {
    throw new Error("Elige una sola periodicidad recurrente para el contrato.");
  }
  if (
    ((input.billing_setup_amount ?? 0) > 0 && !columns.has("billing_setup_amount")) ||
    ((input.billing_monthly_amount ?? 0) > 0 && !columns.has("billing_monthly_amount"))
  ) {
    throw new Error("Falta aplicar la migración de cobros del directorio en Supabase.");
  }

  const payload: Record<string, unknown> = {
    origin: "manual",
    saas_store_id: null,
    slug,
    name: input.name.trim(),
    city: input.city.trim(),
    category: input.category.trim(),
    store_url: (input.website_url ?? "").trim(),
    description: input.description?.trim() || null,
    is_visible_in_directory: input.publish,
    is_force_hidden: false,
  };

  const contact: Record<string, unknown> = {
    department: input.department?.trim() || null,
    province: input.province?.trim() || null,
    district: input.district?.trim() || null,
    address: input.address?.trim() || null,
    phone: input.phone?.trim() || null,
    whatsapp: input.whatsapp?.trim() || null,
    website_url: input.website_url?.trim() || null,
    google_maps_url: input.google_maps_url?.trim() || null,
    logo_url: input.logo_url ?? null,
    cover_image_url: input.cover_image_url ?? null,
    gallery: input.gallery ?? [],
    social_links: input.social_links ?? {},
  };

  if (columns.has("category_id")) payload["category_id"] = input.category_id ?? null;
  if (columns.has("keywords")) payload["keywords"] = input.keywords ?? [];
  for (const [key, value] of Object.entries({
    billing_plan_id: input.billing_plan_id ?? null,
    billing_plan_name: input.billing_plan_name ?? null,
    billing_plan_price: input.billing_plan_price ?? null,
    billing_plan_period: input.billing_plan_period ?? null,
    billing_plan_snapshot: input.billing_plan_snapshot ?? null,
    billing_is_comped: input.billing_is_comped ?? false,
    billing_price_note: input.billing_price_note?.trim() || null,
    billing_setup_amount: input.billing_setup_amount ?? 0,
    billing_setup_due_date: input.billing_setup_due_date ?? null,
    billing_monthly_amount: input.billing_monthly_amount ?? 0,
    billing_yearly_amount: input.billing_yearly_amount ?? 0,
    billing_start_date: input.billing_start_date ?? null,
  })) {
    if (columns.has(key)) payload[key] = value;
  }

  for (const [key, value] of Object.entries(contact)) {
    if (columns.has(key)) payload[key] = value;
  }
  if (columns.has("latitude") && input.latitude != null) payload.latitude = input.latitude;
  if (columns.has("longitude") && input.longitude != null) payload.longitude = input.longitude;
  if (columns.has("editorial_status")) payload.editorial_status = input.publish ? "visible" : "review";

  const { data, error } = await supabase
    .from("stores_directory")
    .insert(payload as never)
    .select("*")
    .single();
  if (error) throw new Error(mapDirectoryError(error));
  return data as StoreDirectoryRow;
}

export interface DirectoryPlanOption {
  id: string;
  name: string;
  slug: string;
  price_amount: number;
  billing_period: "monthly" | "yearly" | "one_time";
  summary: string | null;
  features: unknown;
  directory_priority: 0 | 1 | 2;
}

/** Lista pequeña del catálogo público, filtrada en PostgreSQL para el selector de contratación. */
export async function fetchDirectoryPlans(): Promise<DirectoryPlanOption[]> {
  const { data, error } = (await supabase
    .from("plans")
    .select("id, name, slug, price_amount, billing_period, summary, features, directory_priority")
    .eq("offer_type", "directory")
    .eq("is_published", true)
    .gte("price_amount", 0)
    .order("sort_order", { ascending: true })
    .order("name", { ascending: true })) as unknown as {
    data: Array<Record<string, unknown>> | null;
    error: unknown;
  };
  if (error) throw new Error(mapDirectoryError(error));
  return (data ?? []).map((row) => ({
    id: String(row.id),
    name: String(row.name),
    slug: String(row.slug),
    price_amount: Number(row.price_amount),
    billing_period: row.billing_period as DirectoryPlanOption["billing_period"],
    summary: row.summary == null ? null : String(row.summary),
    features: row.features,
    directory_priority:
      Number(row.directory_priority) === 2 ? 2 : Number(row.directory_priority) === 1 ? 1 : 0,
  }));
}

export async function fetchSubmissions(): Promise<DirectorySubmission[]> {
  const { data, error } = await supabase
    .from("directory_submissions")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function fetchSubmissionsPage(
  queue: "open" | "closed",
  page: number,
  pageSize = 20,
): Promise<{ items: DirectorySubmission[]; total: number }> {
  const statuses: DirectorySubmission["status"][] =
    queue === "open" ? ["pending", "received", "in_review", "changes_requested"] : ["approved", "rejected"];
  const from = (Math.max(1, page) - 1) * pageSize;
  const { data, count, error } = await supabase
    .from("directory_submissions")
    .select("*", { count: "exact" })
    .in("status", statuses)
    .order("created_at", { ascending: queue === "open" })
    .range(from, from + pageSize - 1);
  if (error) throw error;
  const items = (data ?? []) as DirectorySubmission[];
  const ids = items.map((item) => item.id);
  if (ids.length === 0) return { items, total: count ?? 0 };

  const { data: events, error: eventsError } = (await supabase
    .from("directory_submission_review_events" as never)
    .select("*")
    .in("submission_id" as never, ids as never)
    .order("created_at", { ascending: false })) as unknown as {
    data: DirectorySubmissionReviewEvent[] | null;
    error: unknown;
  };
  if (eventsError) throw new Error(mapDirectoryError(eventsError));
  const eventsBySubmission = new Map<string, DirectorySubmissionReviewEvent[]>();
  for (const event of events ?? []) {
    const list = eventsBySubmission.get(event.submission_id) ?? [];
    list.push(event);
    eventsBySubmission.set(event.submission_id, list);
  }
  return {
    items: items.map((item) => ({ ...item, review_events: eventsBySubmission.get(item.id) ?? [] })),
    total: count ?? 0,
  };
}

export async function fetchInvitations(): Promise<DirectoryInvitation[]> {
  const { data, error } = await supabase
    .from("directory_invitations")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  const invitations = (data ?? []) as DirectoryInvitation[];
  const submissionIds = invitations
    .map((invitation) => invitation.submission_id)
    .filter((id): id is string => Boolean(id));
  if (submissionIds.length === 0) return invitations;
  const { data: submissions, error: submissionsError } = (await supabase
    .from("directory_submissions")
    .select("id, status")
    .in("id", submissionIds)) as unknown as {
    data: Array<{ id: string; status: DirectorySubmission["status"] }> | null;
    error: unknown;
  };
  if (submissionsError) throw submissionsError;
  const statusById = new Map((submissions ?? []).map((submission) => [submission.id, submission.status]));
  return invitations.map((invitation) => ({
    ...invitation,
    submission_status: invitation.submission_id ? (statusById.get(invitation.submission_id) ?? null) : null,
  }));
}

export async function fetchInvitationsPage(
  page: number,
  pageSize = 20,
): Promise<{ items: DirectoryInvitation[]; total: number }> {
  const from = (Math.max(1, page) - 1) * pageSize;
  const { data, count, error } = await supabase
    .from("directory_invitations")
    .select("*", { count: "exact" })
    .order("created_at", { ascending: false })
    .range(from, from + pageSize - 1);
  if (error) throw error;
  const invitations = (data ?? []) as DirectoryInvitation[];
  const submissionIds = invitations
    .map((invitation) => invitation.submission_id)
    .filter((id): id is string => Boolean(id));
  if (submissionIds.length === 0) return { items: invitations, total: count ?? 0 };
  const { data: submissions, error: submissionsError } = (await supabase
    .from("directory_submissions")
    .select("id, status")
    .in("id", submissionIds)) as unknown as {
    data: Array<{ id: string; status: DirectorySubmission["status"] }> | null;
    error: unknown;
  };
  if (submissionsError) throw submissionsError;
  const statusById = new Map((submissions ?? []).map((submission) => [submission.id, submission.status]));
  return {
    items: invitations.map((invitation) => ({
      ...invitation,
      submission_status: invitation.submission_id ? (statusById.get(invitation.submission_id) ?? null) : null,
    })),
    total: count ?? 0,
  };
}

export interface CreateInvitationInput {
  label?: string | null;
  singleUse: boolean;
  expiry: InvitationExpiryOption;
  customExpiry?: string | undefined;
  features?: string[];
  billingPlanId?: string | null;
  billingPlanName?: string | null;
  billingPlanPrice?: number | null;
  billingPlanPeriod?: "monthly" | "yearly" | "one_time" | null;
  billingPlanSnapshot?: Record<string, unknown> | null;
  billingIsComped?: boolean;
  billingPriceNote?: string | null;
  billingSetupAmount?: number;
  billingSetupDueDate?: string | undefined;
  billingMonthlyAmount?: number;
  billingYearlyAmount?: number;
  billingStartDate?: string | undefined;
}

export interface CreatedInvitation {
  token: string;
  url: string;
  invitation: DirectoryInvitation;
}

/** Genera una invitacion. El token en claro solo existe aqui; en la BD va su hash. */
export async function createInvitation(input: CreateInvitationInput): Promise<CreatedInvitation> {
  const columns = await getSupportedColumns("directory_invitations");
  if (input.billingPlanId && !columns.has("billing_plan_id")) {
    throw new Error("Aplica primero la migración de planes y acuerdos del directorio en Supabase.");
  }
  if ((input.billingYearlyAmount ?? 0) > 0 && !columns.has("billing_yearly_amount")) {
    throw new Error("Aplica primero la migración de cobros anuales del directorio en Supabase.");
  }
  if (input.billingIsComped && !columns.has("billing_is_comped")) {
    throw new Error("Aplica primero la migración de cortesías gratuitas del directorio en Supabase.");
  }
  if (
    input.billingIsComped &&
    ((input.billingSetupAmount ?? 0) > 0 ||
      (input.billingMonthlyAmount ?? 0) > 0 ||
      (input.billingYearlyAmount ?? 0) > 0)
  ) {
    throw new Error("Una cortesía gratuita no puede tener importes de cobro.");
  }
  if ((input.billingYearlyAmount ?? 0) > 0 && (input.billingMonthlyAmount ?? 0) > 0) {
    throw new Error("Elige una sola periodicidad recurrente para el contrato.");
  }
  const { data: sessionData } = await supabase.auth.getSession();
  const token = generateInvitationToken();
  const tokenHash = await hashInvitationToken(token);
  const expiresAt = computeInvitationExpiry(input.expiry, input.customExpiry);

  const { data, error } = await supabase
    .from("directory_invitations")
    .insert({
      token_hash: tokenHash,
      token_prefix: invitationTokenPrefix(token),
      label: input.label?.trim() || null,
      features: normalizeDirectoryInvitationFeatures(input.features),
      ...(columns.has("billing_plan_id")
        ? {
            billing_plan_id: input.billingPlanId ?? null,
            billing_plan_name: input.billingPlanName ?? null,
            billing_plan_price: input.billingPlanPrice ?? null,
            billing_plan_period: input.billingPlanPeriod ?? null,
            billing_plan_snapshot: input.billingPlanSnapshot ?? null,
            billing_price_note: input.billingPriceNote?.trim() || null,
          }
        : {}),
      ...(columns.has("billing_is_comped") ? { billing_is_comped: input.billingIsComped ?? false } : {}),
      billing_setup_amount: input.billingSetupAmount ?? 0,
      billing_setup_due_date: input.billingSetupDueDate ?? null,
      billing_monthly_amount: input.billingMonthlyAmount ?? 0,
      ...(columns.has("billing_yearly_amount")
        ? { billing_yearly_amount: input.billingYearlyAmount ?? 0 }
        : {}),
      billing_start_date: input.billingStartDate ?? null,
      single_use: input.singleUse,
      expires_at: expiresAt,
      created_by: sessionData.session?.user.id ?? null,
    } as never)
    .select("*")
    .single();
  if (error) throw error;

  return { token, url: buildInvitationUrl(publicSiteUrl, token), invitation: data as DirectoryInvitation };
}

export interface CreateEditInvitationInput {
  storeId: string;
  label?: string | null;
  expiry: InvitationExpiryOption;
  customExpiry?: string | undefined;
  features: string[];
  billingServiceAmount?: number;
  billingMonthlyAmount?: number;
  billingStartDate?: string | undefined;
}

/** Crea un enlace temporal vinculado a una ficha existente; nunca crea solicitudes aquí. */
export async function createEditInvitation(input: CreateEditInvitationInput): Promise<CreatedInvitation> {
  const { data: store, error: storeError } = (await supabase
    .from("stores_directory")
    .select("id, origin")
    .eq("id", input.storeId)
    .maybeSingle()) as unknown as {
    data: { id: string; origin: string | null } | null;
    error: unknown;
  };
  if (storeError) throw new Error(mapDirectoryError(storeError));
  if (!store) throw new Error("Selecciona una ficha existente del directorio.");

  const origin = store.origin === "manual" ? "manual" : "saas";
  const features = normalizeDirectoryEditFeatures(input.features, origin);
  if (features.length === 0) throw new Error("Selecciona al menos un campo para editar.");

  const { data: sessionData } = await supabase.auth.getSession();
  const token = generateInvitationToken();
  const tokenHash = await hashInvitationToken(token);
  const expiresAt = computeInvitationExpiry(input.expiry, input.customExpiry);

  const { data, error } = await supabase
    .from("directory_invitations")
    .insert({
      token_hash: tokenHash,
      token_prefix: invitationTokenPrefix(token),
      label: input.label?.trim() || null,
      mode: "edit",
      target_store_id: store.id,
      features,
      billing_service_amount: input.billingServiceAmount ?? 0,
      billing_monthly_amount: input.billingMonthlyAmount ?? 0,
      billing_start_date: input.billingStartDate ?? null,
      single_use: false,
      expires_at: expiresAt,
      created_by: sessionData.session?.user.id ?? null,
    } as never)
    .select("*")
    .single();
  if (error) throw error;

  return {
    token,
    url: `${publicSiteUrl}/editar-ficha/${token}`,
    invitation: data as DirectoryInvitation,
  };
}

export interface DirectoryBillingCharge {
  id: string;
  store_id: string | null;
  store_name: string;
  charge_type: "setup" | "monthly" | "yearly" | "edit";
  description: string;
  amount: number;
  currency: "PEN";
  due_date: string;
  period_start: string | null;
  status: "pending" | "paid" | "void";
  paid_at: string | null;
  created_at: string;
  contact_phone: string | null;
  last_reminder_at: string | null;
  reminder_count: number;
}

export interface DirectoryStore360Data {
  store: StoreDirectoryRow;
  submissions: DirectorySubmission[];
  invitations: DirectoryInvitation[];
  charges: DirectoryBillingCharge[];
  billingSummary: DirectoryBillingStoreSummary;
}

export interface DirectoryBillingStoreSummary {
  charge_count: number;
  pending_count: number;
  pending_total: number;
  overdue_count: number;
  overdue_total: number;
  paid_count: number;
  paid_total: number;
}

/** Loads a bounded, business-specific operational view for the 360 page. */
export async function fetchDirectoryStore360(storeId: string): Promise<DirectoryStore360Data> {
  const store = await fetchDirectoryStore(storeId);
  const today = new Date();
  const localToday = new Date(today.getTime() - today.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 10);
  const [submissionResult, chargeResult, targetInvitationResult, billingSummaryResult] = await Promise.all([
    supabase
      .from("directory_submissions")
      .select("*")
      .eq("store_id", storeId)
      .order("created_at", { ascending: false })
      .limit(30),
    supabase
      .from("directory_billing_charges")
      .select("*")
      .eq("store_id", storeId)
      .order("created_at", { ascending: false })
      .limit(50),
    supabase
      .from("directory_invitations")
      .select("*")
      .eq("target_store_id", storeId)
      .order("created_at", { ascending: false })
      .limit(50),
    supabase.rpc(
      "directory_billing_store_summary" as never,
      { p_store_id: storeId, p_today: localToday } as never,
    ),
  ]);
  if (submissionResult.error) throw new Error(mapDirectoryError(submissionResult.error));
  if (chargeResult.error) throw new Error(mapDirectoryError(chargeResult.error));
  if (targetInvitationResult.error) throw new Error(mapDirectoryError(targetInvitationResult.error));
  if (billingSummaryResult.error) throw new Error(mapDirectoryError(billingSummaryResult.error));

  const submissions = (submissionResult.data ?? []) as DirectorySubmission[];
  const submissionIds = submissions.map((submission) => submission.id);
  let linkedInvitations: DirectoryInvitation[] = [];
  let reviewEvents: DirectorySubmissionReviewEvent[] = [];
  if (submissionIds.length > 0) {
    const [linkedResult, reviewResult] = await Promise.all([
      supabase
        .from("directory_invitations")
        .select("*")
        .in("submission_id", submissionIds)
        .order("created_at", { ascending: false })
        .limit(50),
      supabase
        .from("directory_submission_review_events" as never)
        .select("*")
        .in("submission_id" as never, submissionIds as never)
        .order("created_at", { ascending: false }) as unknown as Promise<{
        data: DirectorySubmissionReviewEvent[] | null;
        error: unknown;
      }>,
    ]);
    if (linkedResult.error) throw new Error(mapDirectoryError(linkedResult.error));
    if (reviewResult.error) throw new Error(mapDirectoryError(reviewResult.error));
    linkedInvitations = (linkedResult.data ?? []) as DirectoryInvitation[];
    reviewEvents = reviewResult.data ?? [];
  }

  const eventsBySubmission = new Map<string, DirectorySubmissionReviewEvent[]>();
  for (const event of reviewEvents) {
    const list = eventsBySubmission.get(event.submission_id) ?? [];
    list.push(event);
    eventsBySubmission.set(event.submission_id, list);
  }
  const submissionsWithEvents = submissions.map((submission) => ({
    ...submission,
    review_events: eventsBySubmission.get(submission.id) ?? [],
  }));
  const invitationsById = new Map<string, DirectoryInvitation>();
  for (const invitation of [
    ...(targetInvitationResult.data ?? []),
    ...linkedInvitations,
  ] as DirectoryInvitation[]) {
    invitationsById.set(invitation.id, invitation);
  }
  return {
    store,
    submissions: submissionsWithEvents,
    invitations: [...invitationsById.values()]
      .sort((left, right) => right.created_at.localeCompare(left.created_at))
      .slice(0, 50),
    charges: (chargeResult.data ?? []) as DirectoryBillingCharge[],
    billingSummary: billingSummaryResult.data as unknown as DirectoryBillingStoreSummary,
  };
}

export interface DirectoryBillingMetrics {
  pending_count: number;
  pending_total: number;
  overdue_count: number;
  overdue_total: number;
  upcoming_count: number;
  upcoming_total: number;
  paid_count: number;
  paid_total: number;
}

export interface DirectoryBillingQueue {
  items: DirectoryBillingCharge[];
  total_count: number;
  metrics: DirectoryBillingMetrics;
}

export async function fetchDirectoryBillingCharges(input: {
  bucket: "all" | "pending" | "overdue" | "upcoming" | "paid";
  search: string;
  page: number;
  today: string;
}): Promise<DirectoryBillingQueue> {
  const { data, error } = await supabase.rpc(
    "directory_billing_work_queue" as never,
    {
      p_bucket: input.bucket,
      p_search: input.search,
      p_page: input.page,
      p_page_size: 30,
      p_today: input.today,
    } as never,
  );
  if (error) throw error;
  return data as unknown as DirectoryBillingQueue;
}

export async function registerDirectoryBillingReminder(id: string): Promise<void> {
  const { error } = await supabase.rpc(
    "directory_billing_register_reminder" as never,
    { p_charge_id: id } as never,
  );
  if (error) throw error;
}

export async function markDirectoryBillingChargePaid(id: string): Promise<void> {
  const { data: sessionData } = await supabase.auth.getSession();
  const { error } = await supabase
    .from("directory_billing_charges")
    .update({
      status: "paid",
      paid_at: new Date().toISOString(),
      paid_by: sessionData.session?.user.id ?? null,
    } as never)
    .eq("id", id);
  if (error) throw error;
}

export async function markDirectoryBillingChargePending(id: string): Promise<void> {
  const { error } = await supabase
    .from("directory_billing_charges")
    .update({ status: "pending", paid_at: null, paid_by: null } as never)
    .eq("id", id);
  if (error) throw error;
}

export async function generateRecurringBillingCharges(): Promise<number> {
  const { data, error } = await supabase.rpc(
    "directory_billing_generate_monthly_charges" as never,
    {} as never,
  );
  if (error) throw error;
  return Number(data ?? 0);
}

export interface DirectoryBillingAccount {
  id: string;
  name: string;
  billing_plan_name: string | null;
  billing_plan_period?: "monthly" | "yearly" | "one_time" | null;
  billing_is_comped?: boolean;
  billing_price_note?: string | null;
  billing_monthly_amount: number;
  billing_yearly_amount?: number;
  billing_amount: number;
  billing_period: "monthly" | "yearly" | "one_time" | null;
  billing_start_date: string | null;
  billing_status: "active" | "paused" | "cancelled";
}

export async function fetchDirectoryBillingAccounts(input: {
  search: string;
  status: string;
  page: number;
  pageSize?: number;
}): Promise<{ items: DirectoryBillingAccount[]; total: number }> {
  const pageSize = input.pageSize ?? 20;
  const columns = await getSupportedColumns("stores_directory");
  const selected = [
    "id",
    "name",
    ...(columns.has("billing_plan_name") ? ["billing_plan_name"] : []),
    ...(columns.has("billing_plan_period") ? ["billing_plan_period"] : []),
    ...(columns.has("billing_is_comped") ? ["billing_is_comped"] : []),
    ...(columns.has("billing_price_note") ? ["billing_price_note"] : []),
    "billing_monthly_amount",
    ...(columns.has("billing_yearly_amount") ? ["billing_yearly_amount"] : []),
    "billing_start_date",
    "billing_status",
  ].join(", ");
  let query = supabase
    .from("stores_directory")
    .select(selected, { count: "exact" })
    .order("name", { ascending: true })
    .order("id", { ascending: true });
  if (columns.has("billing_is_comped")) {
    const filters = ["billing_monthly_amount.gt.0"];
    if (columns.has("billing_yearly_amount")) filters.push("billing_yearly_amount.gt.0");
    filters.push("billing_is_comped.eq.true");
    query = query.or(filters.join(","));
  } else if (columns.has("billing_yearly_amount")) {
    query = query.or("billing_monthly_amount.gt.0,billing_yearly_amount.gt.0");
  } else {
    query = query.gt("billing_monthly_amount", 0);
  }
  if (input.search.trim()) query = query.ilike("name", `%${input.search.trim()}%`);
  if (input.status !== "all") query = query.eq("billing_status", input.status);
  const from = (Math.max(1, input.page) - 1) * pageSize;
  const { data, count, error } = (await query.range(from, from + pageSize - 1)) as unknown as {
    data: DirectoryBillingAccount[] | null;
    count: number | null;
    error: unknown;
  };
  if (error) throw new Error(mapDirectoryError(error));
  return {
    items: (data ?? []).map((account) => {
      const annualAmount = Number(account.billing_yearly_amount ?? 0);
      const isComped = account.billing_is_comped === true;
      const period =
        annualAmount > 0
          ? ("yearly" as const)
          : Number(account.billing_monthly_amount) > 0
            ? ("monthly" as const)
            : null;
      return {
        ...account,
        billing_plan_name: account.billing_plan_name ?? null,
        billing_is_comped: isComped,
        billing_price_note: account.billing_price_note ?? null,
        billing_amount: isComped
          ? 0
          : annualAmount > 0
            ? annualAmount
            : Number(account.billing_monthly_amount),
        billing_period: period ?? (account.billing_plan_period === "one_time" ? ("one_time" as const) : null),
      };
    }),
    total: count ?? 0,
  };
}

export async function updateDirectoryBillingStatus(
  storeId: string,
  status: DirectoryBillingAccount["billing_status"],
): Promise<void> {
  const { error } = await supabase.rpc(
    "directory_billing_set_status" as never,
    {
      p_store_id: storeId,
      p_status: status,
    } as never,
  );
  if (error) throw error;
}

export async function revokeInvitation(id: string): Promise<void> {
  const { error } = await supabase
    .from("directory_invitations")
    .update({ status: "revoked", revoked_at: new Date().toISOString() } as never)
    .eq("id", id);
  if (error) throw error;
}

export type ReviewAction = "approve" | "reject" | "request_changes" | "reopen";

export interface ReviewResult {
  status: string;
  store_id?: string;
  error?: string;
}

/** Invoca la Edge Function de revision (valida rol admin/editor en servidor). */
export async function reviewSubmission(
  submissionId: string,
  action: ReviewAction,
  notes?: string,
): Promise<ReviewResult> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) throw new Error("Tu sesion ha caducado. Vuelve a iniciar sesion.");

  const response = await fetch(REVIEW_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({ action, submission_id: submissionId, notes }),
  });
  const payload = (await response.json().catch(() => ({}))) as ReviewResult;
  if (!response.ok) throw new Error(payload.error ?? `review_failed_${response.status}`);
  return payload;
}

/** URL firmada y temporal para previsualizar archivos de una solicitud pendiente. */
export async function signedSubmissionFileUrl(path: string | null): Promise<string | null> {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const { data, error } = await supabase.storage.from(SUBMISSIONS_BUCKET).createSignedUrl(path, 3600);
  if (error) return null;
  return data?.signedUrl ?? null;
}
